import React, { createContext, useCallback, useEffect, useState } from "react";
import { Color } from "@material-ui/lab/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { getErrorInfo } from "store/features/general/general.selectors";
import { resetUnhandledError } from "store/features/general/general.actions";
import { useBackdrop } from "hooks";

interface AlertProps {
    toggleAlert(props: { message: string; type: Color }): void;
    isToggled: boolean;
    message: string;
    type?: Color;
}

interface AlertState {
    isToggled: boolean;
    message: string;
    type?: Color;
}

const TIMEOUT_DELAY = 6000;

export const AlertContext = createContext({} as AlertProps);

export const AlertProvider: React.FC = ({ children }) => {
    const dispatch = useDispatch();
    const { hasError, message } = useSelector(getErrorInfo);
    const { hideBackdrop } = useBackdrop();
    const [state, setState] = useState<AlertState>({
        isToggled: false,
        message: "",
        type: undefined,
    });

    const toggleAlert = useCallback(
        ({ message, type }) =>
            setState({
                isToggled: true,
                message,
                type,
            }),
        []
    );

    useEffect(() => {
        if (hasError) {
            hideBackdrop();
            setState({ message, type: "error", isToggled: true });
        }
    }, [hasError, message, hideBackdrop]);

    useEffect(() => {
        if (!state.isToggled) {
            return;
        }

        const timer = setTimeout(() => {
            // reset global errors
            if (hasError) {
                dispatch(resetUnhandledError());
            }

            setState({
                message: "",
                type: undefined,
                isToggled: false,
            });
        }, TIMEOUT_DELAY);

        return (): void => {
            clearTimeout(timer);
        };
    }, [state.isToggled, hasError, dispatch]);

    return <AlertContext.Provider value={{ ...state, toggleAlert }}>{children}</AlertContext.Provider>;
};
